/**
 * SaldosDeBeneficios — una tarjeta por beneficio de días de la ficha (#1765).
 *
 * «Días administrativos · 2 de 3 disponibles · 1 en trámite». El saldo lo calcula
 * el backend; acá solo se muestra. Lo usan la ficha (ERP) y el portal.
 *
 * Sin beneficios no se dibuja nada: una tarjeta vacía en una empresa que no los
 * usa es ruido.
 */
import { formatDate } from '@/lib/locale'
import { formatoDias, type SaldoDeBeneficio } from '../hooks/useBeneficios'

interface Props {
  saldos: SaldoDeBeneficio[]
  locale?: string
  /** Beneficios activos que la ficha no tiene (solo en el ERP: explica por qué no aparecen). */
  sinBeneficio?: { beneficio: string; nombre: string }[]
}

export function SaldosDeBeneficios({ saldos, locale = 'es-CL', sinBeneficio = [] }: Props) {
  if (!saldos.length && !sinBeneficio.length) return null
  return (
    <div className="space-y-2">
      {saldos.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {saldos.map(s => <TarjetaDeSaldo key={s.beneficio} s={s} locale={locale} />)}
        </div>
      )}
      {sinBeneficio.length > 0 && (
        <p className="text-[11px] text-content-subtle leading-snug">
          Sin {sinBeneficio.map(b => `«${b.nombre}»`).join(', ')}: la ficha está excluida o el
          beneficio aplica solo a fichas asignadas (Nómina → Configuración → Beneficios de días).
        </p>
      )}
    </div>
  )
}

function TarjetaDeSaldo({ s, locale }: { s: SaldoDeBeneficio; locale: string }) {
  const total = parseFloat(s.asignado) + parseFloat(s.arrastre)
  const disponible = parseFloat(s.disponible)
  const enTramite = parseFloat(s.en_tramite)
  const excedido = parseFloat(s.excedido)
  const fechas = s.fechas_validas

  return (
    <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-3 shadow-card">
      <p className="text-[11px] uppercase tracking-wide text-content-subtle">{s.nombre}</p>
      <p className="text-xl font-bold text-content">
        {formatoDias(disponible, locale)}
        <span className="text-sm font-normal text-content-muted">
          {' '}de {formatoDias(total, locale)} {total === 1 ? 'día disponible' : 'días disponibles'}
        </span>
      </p>
      <p className="text-xs text-content-muted mt-0.5">
        Usados {formatoDias(s.usados, locale)}
        {enTramite > 0 && <span className="text-warning-content-soft"> · {formatoDias(enTramite, locale)} en trámite</span>}
        {parseFloat(s.arrastre) > 0 && <> · incluye {formatoDias(s.arrastre, locale)} del período anterior</>}
      </p>
      {excedido > 0 && (
        <p className="text-xs text-warning-content-soft mt-0.5">
          Se usaron {formatoDias(excedido, locale)} más de lo que corresponde en el período.
        </p>
      )}
      <p className="text-[11px] text-content-subtle mt-1">
        Período {formatDate(s.periodo_inicio, locale)} – {formatDate(s.periodo_fin, locale)}
        {s.fecha_de_referencia && fechas && fechas.length > 0 && (
          <> · {fechas.length === 1 ? 'fecha' : 'fechas'}: {fechas.map(f => formatDate(f, locale)).join(', ')}</>
        )}
        {s.fecha_de_referencia && fechas && fechas.length === 0 && (
          <> · sin fecha válida en el período (falta la fecha de {s.fecha_de_referencia === 'cumpleanos' ? 'nacimiento' : 'ingreso'} en la ficha, o la regla no deja ningún día)</>
        )}
      </p>
    </div>
  )
}
